/**
 * LocationJsonLd — injects JSON-LD structured data into <head> for a
 * location article. Emits two graph nodes:
 *   • schema.org "Place" (or AdministrativeArea for country/state/county)
 *   • schema.org "BreadcrumbList" for the Country › State › City chain
 *
 * Google's rich-result docs consider `Place` sufficient for
 * geographic entities; we upgrade to `AdministrativeArea`/`City`
 * subtypes when the kind is known, which gives us cleaner sitelinks
 * in SERPs.
 */
import { useEffect } from "react";

const ORIGIN = process.env.REACT_APP_BACKEND_URL || "";

const KIND_TYPE = {
  country:  "Country",
  state:    "AdministrativeArea",
  district: "AdministrativeArea",
  county:   "AdministrativeArea",
  city:     "City",
  town:     "City",
  village:  "City",
};

function absUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  return `${ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

export default function LocationJsonLd({ doc }) {
  useEffect(() => {
    if (!doc?.slug) return;
    const type = KIND_TYPE[(doc.kind || "").toLowerCase()] || "Place";

    const place = {
      "@context": "https://schema.org",
      "@type":    type,
      "name":     doc.name,
      "url":      absUrl(`/l/${doc.slug}`),
      "identifier": doc.slug,
    };

    // Coordinates → GeoCoordinates block
    if (Number.isFinite(doc.lat) && Number.isFinite(doc.lng)) {
      place.geo = {
        "@type":     "GeoCoordinates",
        "latitude":  doc.lat,
        "longitude": doc.lng,
      };
    }

    // "Contains" hierarchy: country > state > city
    const containedIn = [];
    if (doc.country && doc.kind !== "country") {
      containedIn.push({ "@type": "Country", "name": doc.country });
    }
    if (doc.state && doc.kind !== "state" && doc.kind !== "country") {
      containedIn.push({ "@type": "AdministrativeArea", "name": doc.state });
    }
    if (containedIn.length) {
      place.containedInPlace = containedIn.length === 1 ? containedIn[0] : containedIn;
    }

    // A short description if the overview section is present
    const overview = doc.sections?.overview || doc.sections?.basic;
    if (overview) {
      place.description = String(overview).replace(/\s+/g, " ").trim().slice(0, 400);
    }

    // Wikipedia sameAs (helps entity resolution)
    const wiki = (doc.links || []).find((l) => l.kind === "wikipedia");
    if (wiki?.url) place.sameAs = [wiki.url];

    // Breadcrumbs
    const segs = (doc.slug || "").split("/").filter(Boolean);
    const labels = [];
    if (doc.country) labels.push(doc.country);
    if (doc.state) labels.push(doc.state);
    if (doc.city && doc.kind !== "country" && doc.kind !== "state") labels.push(doc.city);
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": labels.map((name, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": name,
        "item": absUrl("/l/" + segs.slice(0, i + 1).join("/")),
      })),
    };

    const payload = JSON.stringify([place, breadcrumb]);
    const id = `loc-jsonld-${doc.slug}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = payload;

    return () => {
      const node = document.getElementById(id);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [doc?.slug, doc?.kind, doc?.name, doc?.lat, doc?.lng, doc?.country, doc?.state, doc?.city, doc?.sections?.overview, doc?.sections?.basic, doc?.links]);

  return null;
}
